import { find, findPayloadT } from '@execonline-inc/collections';
import { toTask } from '@execonline-inc/maybe-adapter';
import { always, assertNever, noop } from '@kofno/piper';
import { Maybe, fromEmpty, fromNullable, just, nothing } from 'maybeasy';
import { action, computed, makeObservable, observable } from 'mobx';
import { DropResult } from '@hello-pangea/dnd';
import { Task } from 'taskarian';
import { getSearchParametersFromUrl } from '../../../../../ListFilter';
import { SearchParameter } from '../../../../../ListFilter/Types';
import { findInOptions } from '../../../../../Resource/FormTemplates/Inputs/MultipleSelectionsInput';
import {
  FormTemplate,
  findField,
  findFieldT,
  stringFieldOutValue,
} from '../../../../../Resource/FormTemplates/Types';
import { Link } from '../../../../../Resource/Types';
import {
  ExperienceResource,
  LearningCollectionResource,
  LearningDesignResource,
  ProductCollectionDisplayType,
  ProductCollectionResource,
  ProductLicenseResource,
  ProductLicensesResource,
  RecommendationEngineResource,
  allCoachingExperiencesFrom,
  filterProductCollectionBy,
  isEcommerceEnabled,
} from '../../../../LearningCollections/Types';
import { WizardStepItem } from '../../../../WizardStepGroup/Types';
import LearningCollectionsStore from '../../../Store';
import { ShowUnlicensedToggle } from '../../CurrentCollectionStep/Builder/ExperienceCatalog/Types';
import LearningDesignExperienceStore from '../../Experience/Store';
import LearningDesignsStore from '../../LearningDesigns/Store';
import ProductLicensesStore from '../../ProductLicenses/Store';
import {
  LearningCollectionError,
  LearningDesignSection,
  ModifiableExperiencesContext,
  Section,
  SectionContext,
  isSameSection,
  missingTemplates,
} from '../../Types';
import { FileMetaData } from '../CurrentCollectionStep/Details/S3ImageUpload/Types';
import IframeContentStore from '../CurrentCollectionStep/Preview/IframeContent/Store';
import PreviewStore from '../CurrentCollectionStep/Preview/Store';
import PublishingStore from '../CurrentCollectionStep/Share/PublishSection/Store';
import {
  AddingLearningDesign,
  Building,
  BuildingError,
  ChoosingLearningDesigns,
  LearningCollectionEditFormResource,
  LearningCollectionEditableStep,
  LearningCollectionState,
  ProductCollectionLicenseUpdatedResource,
  SelectingProductLicenseError,
  Visibility,
  addExperienceStore,
  addExperienceStoreWithSelectedIds,
  addingLearningDesign,
  browseAllFieldValue,
  browseAllTemplate,
  building,
  buildingFromUpdatingLicense,
  bulkAddAllPrograms,
  byoTemplate,
  choosingLearningDesigns,
  createSection,
  deletingSection,
  discardChanges,
  editFormSubmissionError,
  editingDetails,
  findBooleanField,
  finished,
  finishing,
  learningDesignTemplate,
  loadError,
  loading,
  loadingForDeepLinks,
  loadingForInvitations,
  modifiableExperiences,
  previewShaHashGenerator,
  previewing,
  previewingUpdating,
  productLicenseChangeDialog,
  ready,
  readyDeepLinks,
  readyInvitations,
  readySavingDetails,
  removeExperienceStore,
  reorder,
  savingDetails,
  sharing,
  toggleAllExperienceStores,
  updatedSearchParameters,
  updating,
  updatingProductLicense,
  waiting,
} from './Types';

class LearningCollectionEditStore {
  @observable
  state: LearningCollectionState = waiting(getSearchParametersFromUrl());

  readonly learningCollectionsStore: LearningCollectionsStore;
  readonly learningDesignsStore = new LearningDesignsStore();
  readonly publishingStore = new PublishingStore();
  readonly productLicensesStore = new ProductLicensesStore();

  constructor(learningCollectionsStore: LearningCollectionsStore) {
    this.learningCollectionsStore = learningCollectionsStore;
    makeObservable(this);
  }

  @action
  loadingForDeepLinks = (id: string) => {
    switch (this.state.kind) {
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'loading-for-deep-links':
      case 'updating':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'previewing-updating':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        break;
      case 'ready-deep-links':
      case 'waiting':
        this.state = loadingForDeepLinks(id, this.state.deepLinksSearchParams);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updatingDeepLinksSearchParams = (searchParameter: SearchParameter) => {
    switch (this.state.kind) {
      case 'ready-deep-links':
        const searchParameters = updatedSearchParameters(searchParameter, this.state);
        this.state = loadingForDeepLinks(
          String(this.state.learningCollectionResource.payload.id),
          searchParameters,
        );
        break;
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'loading-for-deep-links':
      case 'updating':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'previewing-updating':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
      case 'waiting':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  loadingForInvitations = (id: string) => {
    switch (this.state.kind) {
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'loading-for-deep-links':
      case 'loading-for-invitations':
      case 'updating':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'previewing-updating':
      case 'ready-deep-links':
      case 'updating-product-license':
        break;
      case 'ready-invitations':
      case 'waiting':
        this.state = loadingForInvitations(id);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  ready = (
    learningCollectionEditFormResource: LearningCollectionEditFormResource,
    template: FormTemplate,
  ) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'editing-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'updating':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'previewing-updating':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        break;
      case 'saving-details':
        this.state = readySavingDetails(learningCollectionEditFormResource, template, this.state);
        break;
      case 'loading':
        this.state = ready(learningCollectionEditFormResource, template, this.state);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updateSectionTemplate = (
    section: LearningDesignSection,
    formName: string,
    formDescription: string,
    formDisplayType: string,
    formDisplayButtonLabel: string,
  ) => {
    switch (this.state.kind) {
      case 'building':
        findField('name', 'string', section.template.fields).do((name) => (name.value = formName));
        findField('description', 'string', section.template.fields).do(
          (desc) => (desc.value = formDescription),
        );
        findField('display_type', 'string', section.template.fields).do(
          (displayType) => (displayType.value = formDisplayType),
        );
        findField('display_button_label', 'string', section.template.fields).do(
          (displayButtonLabel) => (displayButtonLabel.value = formDisplayButtonLabel),
        );
        break;
      case 'waiting':
      case 'loading':
      case 'load-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'previewing-updating':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'updating':
      case 'edit-form-submission-error':
      case 'updating-product-license':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  building = () => {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'load-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
        break;
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'previewing-updating':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'updating':
      case 'edit-form-submission-error':
      case 'updating-product-license':
        this.buildingWithCelebrationPage(this.state.celebrationPage);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  buildingWithCelebrationPage = (celebrationPage: Visibility) => {
    const state = this.state;
    switch (state.kind) {
      case 'waiting':
      case 'editing-details':
      case 'building':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'updating':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'previewing-updating':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
      case 'loading':
        break;
      case 'previewing':
      case 'ready':
      case 'saving-details':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
        this.state = building({ ...state, celebrationPage });
        break;
      default:
        assertNever(state);
    }
  };

  @action
  choosingLearningDesigns = () => {
    const state = this.state;
    switch (state.kind) {
      case 'waiting':
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'choosing-learning-designs':
      case 'deleting-section':
      case 'previewing':
      case 'previewing-updating':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        break;
      case 'building':
      case 'adding-learning-design':
      case 'edit-form-submission-error':
        this.state = choosingLearningDesigns(state);
        break;
      default:
        assertNever(state);
    }
  };

  @action
  createByoLearningDesign = () => {
    const state = this.state;
    switch (state.kind) {
      case 'waiting':
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'previewing-updating':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        break;
      case 'building':
        byoTemplate(state).fork(noop, (template) => {
          this.state = addingLearningDesign(state, template);
        });
        break;
      default:
        assertNever(state);
    }
  };

  @action
  addBrowseAll = () => {
    const state = this.state;
    switch (state.kind) {
      case 'waiting':
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'previewing-updating':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        break;
      case 'building':
        Task.succeed<BuildingError, Building>(state)
          .assign('template', browseAllTemplate)
          .assign('field', findBooleanField('browse_all'))
          .fork(noop, ({ template, field }) => {
            field.value = true;
            return (this.state = addingLearningDesign(state, template));
          });
        break;

      default:
        assertNever(state);
    }
  };

  @action
  chooseLearningDesign = (learningDesign: LearningDesignResource) => {
    const state = this.state;
    switch (state.kind) {
      case 'waiting':
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'previewing-updating':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        break;
      case 'choosing-learning-designs':
        Task.succeed<BuildingError, ChoosingLearningDesigns>(state)
          .andThen(learningDesignTemplate)
          .andThen((template) =>
            findFieldT('id', 'single-selection', template.fields)
              .map((f) => (f.value = findInOptions(String(learningDesign.payload.id), f.options)))
              .andThen(() =>
                findFieldT('name', 'string', template.fields).map(
                  (f) => (f.value = learningDesign.payload.name.text),
                ),
              )
              .andThen(() =>
                findFieldT('description', 'string', template.fields).map(
                  (f) => (f.value = learningDesign.payload.description),
                ),
              )
              .andThen(() =>
                findFieldT('description', 'string', template.fields).map(
                  (f) => (f.value = learningDesign.payload.description),
                ),
              )
              .map(always(template)),
          )
          .fork(noop, (template) => (this.state = addingLearningDesign(state, template)));
        break;
      default:
        assertNever(state);
    }
  };

  @action
  deletingSection = (section: Section, link: Link) => {
    const state = this.state;
    switch (state.kind) {
      case 'waiting':
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'previewing-updating':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        break;
      case 'building':
        this.state = deletingSection(state, section, link);
        break;
      default:
        assertNever(state);
    }
  };

  @action
  buildingWithNewLearningDesign = (resource: LearningDesignResource) => {
    const state = this.state;
    switch (state.kind) {
      case 'waiting':
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'deleting-section':
      case 'previewing':
      case 'previewing-updating':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        break;
      case 'adding-learning-design':
        const sections = state.sections.filter((section) => !browseAllFieldValue(section.template));
        const browseAllSection = state.sections.filter((section) =>
          browseAllFieldValue(section.template),
        );
        this.state = fromNullable(resource.templates.self)
          .map(createSection(resource, state.learningCollectionEditFormResource))
          .map(
            (section): AddingLearningDesign => ({
              ...state,
              sections: [...sections, section, ...browseAllSection],
            }),
          )
          .map<LearningCollectionState>(building)
          .getOrElseValue(state);
        break;
      default:
        assertNever(state);
    }
  };

  @action
  buildingWithoutSection = () => {
    const state = this.state;
    switch (state.kind) {
      case 'waiting':
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'previewing':
      case 'previewing-updating':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        break;
      case 'deleting-section':
        this.state = building({
          ...state,
          sections: state.sections.filter((s) => !isSameSection(s, state.sectionToDelete)),
        });
        break;
      default:
        assertNever(state);
    }
  };

  @action
  editingDetails = (celebrationPage: Visibility) => {
    const state = this.state;
    switch (state.kind) {
      case 'waiting':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'previewing-updating':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
      case 'previewing':
      case 'editing-details':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
        break;
      case 'ready':
      case 'building':
      case 'saving-details':
        this.state = editingDetails({ ...state, celebrationPage });
        break;
      default:
        assertNever(state);
    }
  };

  @action
  savingDetails = (preview: Maybe<FileMetaData>) => {
    const state = this.state;
    switch (state.kind) {
      case 'waiting':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'previewing-updating':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
      case 'previewing':
      case 'ready':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'building':
      case 'saving-details':
        break;
      case 'editing-details':
        this.state = savingDetails(state, preview);
        break;
      default:
        assertNever(state);
    }
  };

  @action
  saving = () => {
    const state = this.state;
    switch (state.kind) {
      case 'waiting':
      case 'ready':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'previewing-updating':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
      case 'editing-details':
      case 'saving-details':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
        break;
      case 'building':
        const form = state.learningCollectionEditFormResource;
        this.state = updating(form as LearningCollectionEditFormResource, state);
        break;
      case 'edit-form-submission-error':
        this.state = updating(state.learningCollectionEditFormResource, state);
        break;
      default:
        assertNever(state);
    }
  };

  @action
  previewingUpdating = (iframeContentStore: IframeContentStore, previewStore: PreviewStore) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'previewing-updating':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        break;
      case 'previewing':
      case 'edit-form-submission-error':
        this.state = previewingUpdating(
          this.state.learningCollectionEditFormResource,
          this.state,
          iframeContentStore,
          previewStore,
        );
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  sharing = () => {
    switch (this.state.kind) {
      case 'waiting':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'sharing':
      case 'finishing':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'previewing-updating':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        break;
      case 'previewing':
      case 'finished':
      case 'ready':
        this.state = sharing(this.state);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  finishing = () => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'finishing':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'previewing-updating':
      case 'previewing':
      case 'finished':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        break;
      case 'sharing':
        this.state = finishing(this.state);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  finished = () => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'previewing-updating':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
      case 'sharing':
        break;
      case 'finishing':
        this.state = finished(this.state);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  loading = (id: string, step: LearningCollectionEditableStep) => {
    switch (this.state.kind) {
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'previewing-updating':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        break;
      case 'waiting':
        this.state = loading(id, step);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  readyDeepLinks = (learningCollectionResource: LearningCollectionResource) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'ready':
      case 'updating':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'previewing-updating':
      case 'loading':
      case 'loading-for-invitations':
      case 'updating-product-license':
      case 'ready-invitations':
        break;
      case 'ready-deep-links':
      case 'loading-for-deep-links':
        this.state = readyDeepLinks(learningCollectionResource, this.state.deepLinksSearchParams);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  readyInvitations = (learningCollectionResource: LearningCollectionResource) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'ready':
      case 'updating':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'previewing-updating':
      case 'loading':
      case 'ready-deep-links':
      case 'loading-for-deep-links':
      case 'updating-product-license':
        break;
      case 'loading-for-invitations':
      case 'ready-invitations':
        this.state = readyInvitations(learningCollectionResource);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  previewing = (
    learningCollectionFormResource: LearningCollectionEditFormResource,
    template: FormTemplate,
    celebrationPage: Visibility,
  ) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'previewing':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        break;
      case 'sharing':
      case 'ready':
      case 'updating':
      case 'previewing-updating':
        this.state = previewing(
          learningCollectionFormResource,
          this.state,
          template,
          celebrationPage,
        );
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  buildingFromUpdatingLicense = (values: ProductCollectionLicenseUpdatedResource) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'previewing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'sharing':
      case 'ready':
      case 'updating':
      case 'previewing-updating':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
        break;
      case 'updating-product-license':
        this.state = buildingFromUpdatingLicense(this.state, values);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updatingProductLicense = () => {
    switch (this.state.kind) {
      case 'waiting':
      case 'previewing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'sharing':
      case 'ready':
      case 'updating':
      case 'previewing-updating':
      case 'updating-product-license':
      case 'editing-details':
      case 'saving-details':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
        break;
      case 'building':
        this.state = updatingProductLicense(this.state);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  loadError = (error: LearningCollectionError) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'ready':
      case 'updating':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'previewing-updating':
      case 'ready-deep-links':
      case 'ready-invitations':
        break;
      case 'loading-for-deep-links':
      case 'loading-for-invitations':
      case 'loading':
      case 'updating-product-license':
        this.state = loadError(error);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  templateLoadError = () => {
    switch (this.state.kind) {
      case 'waiting':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'finished':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'loading-for-deep-links':
      case 'loading-for-invitations':
      case 'updating-product-license':
        break;
      case 'ready-invitations':
      case 'ready-deep-links':
      case 'sharing':
      case 'ready':
      case 'updating':
      case 'previewing-updating':
      case 'loading':
      case 'previewing':
      case 'finishing':
        this.state = loadError(missingTemplates);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  editFormSubmissionError = (
    learningCollectionFormResource: LearningCollectionEditFormResource,
    error: LearningCollectionError,
    template: FormTemplate,
  ) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finished':
      case 'finishing':
      case 'loading':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'updating-product-license':
      case 'ready-invitations':
        break;
      case 'updating':
      case 'previewing-updating':
        this.state = editFormSubmissionError(
          learningCollectionFormResource,
          template,
          error,
          this.state,
        );
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  selectProductLicense = (productLicenseId: number) => {
    const state = this.state;
    switch (state.kind) {
      case 'waiting':
      case 'ready':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'edit-form-submission-error':
      case 'previewing-updating':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'editing-details':
      case 'saving-details':
        break;
      case 'updating-product-license':
      case 'building':
        toTask<SelectingProductLicenseError, ProductLicensesResource>(
          { kind: 'missing-product-license' },
          this.productLicensesStore.productLicensesResource,
        )
          .andThen((productLicensesResource) =>
            findPayloadT<ProductLicenseResource>(
              productLicenseId,
              productLicensesResource.payload.activeProductLicenses,
            ),
          )
          .fork(noop, (productLicense) => {
            state.selectedProductLicense = just(productLicense);
            this.updatingProductLicense();
          });
        break;
      default:
        assertNever(state);
    }
  };

  @action
  discardChanges = () => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'edit-form-submission-error':
      case 'previewing-updating':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        break;
      case 'previewing':
        this.state = discardChanges(this.state);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  removeExperienceStore = (
    context: ModifiableExperiencesContext,
    experienceResource: ExperienceResource,
  ) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'previewing-updating':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
      case 'editing-details':
      case 'saving-details':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
        break;
      case 'previewing':
      case 'building':
      case 'edit-form-submission-error':
        this.state = removeExperienceStore(context, this.state, experienceResource);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  addExperienceStore = (
    context: ModifiableExperiencesContext,
    experienceResource: ExperienceResource,
  ) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'previewing-updating':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
      case 'editing-details':
      case 'saving-details':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
        break;
      case 'building':
      case 'previewing':
      case 'edit-form-submission-error':
        this.state = addExperienceStore(context, this.state, experienceResource);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  saveShowWarning = (showWarning: ShowUnlicensedToggle) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'previewing-updating':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
      case 'editing-details':
      case 'saving-details':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
        break;
      case 'building':
      case 'previewing':
      case 'edit-form-submission-error':
        this.state.learningCollectionEditFormResource = {
          ...this.state.learningCollectionEditFormResource,
          payload: {
            ...this.state.learningCollectionEditFormResource.payload,
            warningStatus: showWarning,
          },
        };
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  addExperienceStoreWithSelectedIds = (
    context: ModifiableExperiencesContext,
    experienceResource: ExperienceResource,
    selectedIds: number[],
  ) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'previewing-updating':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
      case 'editing-details':
      case 'saving-details':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
        break;
      case 'building':
      case 'previewing':
      case 'edit-form-submission-error':
        this.state = addExperienceStoreWithSelectedIds(
          context,
          this.state,
          experienceResource,
          selectedIds,
        );
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  bulkAddExperienceStores = (
    context: SectionContext,
    experienceResources: ExperienceResource[],
  ) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'previewing-updating':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'previewing':
      case 'edit-form-submission-error':
      case 'updating-product-license':
      case 'editing-details':
      case 'saving-details':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
        break;
      case 'building':
        this.state = bulkAddAllPrograms(context, this.state, experienceResources);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  toggleAllExperienceStores = (context: ModifiableExperiencesContext) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'previewing-updating':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
      case 'editing-details':
      case 'saving-details':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
        break;
      case 'building':
      case 'edit-form-submission-error':
        this.state = toggleAllExperienceStores(context, this.state);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  setBrowseAll = (browseAll: boolean) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'loading':
      case 'load-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
      case 'editing-details':
      case 'saving-details':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
        break;
      case 'building':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'updating':
      case 'previewing-updating':
      case 'edit-form-submission-error':
        findFieldT('browse_all', 'boolean', this.state.template.fields).fork(noop, (field) => {
          field.value = browseAll;
        });
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updateProductLicenseChangeDialog = (productLicenseChangeDialogValue: Visibility) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'loading':
      case 'load-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'updating':
      case 'previewing-updating':
      case 'edit-form-submission-error':
      case 'editing-details':
      case 'saving-details':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
        break;
      case 'building':
        this.state = productLicenseChangeDialog(this.state, productLicenseChangeDialogValue);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  setLearningDesignDisplayType = (section: Section, displayType: ProductCollectionDisplayType) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'load-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
      case 'editing-details':
      case 'saving-details':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
        break;
      case 'building':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'ready':
      case 'updating':
      case 'previewing-updating':
      case 'edit-form-submission-error':
        findFieldT('display_type', 'string', section.template.fields).fork(noop, (field) => {
          field.value = displayType;
        });
        break;
      default:
        assertNever(this.state);
    }
  };

  @computed
  get selectedProductLicense(): Maybe<ProductLicenseResource> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'load-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
        return nothing();
      case 'updating-product-license':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'editing-details':
      case 'saving-details':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'ready':
      case 'updating':
      case 'edit-form-submission-error':
      case 'previewing-updating':
        return this.state.selectedProductLicense;
    }
  }

  @computed
  get sections(): readonly Section[] {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'load-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
        return [];
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'previewing-updating':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'updating':
      case 'edit-form-submission-error':
      case 'updating-product-license':
        return this.state.sections;
    }
  }

  allExperiencesSelected(context: ModifiableExperiencesContext): boolean {
    const state = this.state;
    switch (state.kind) {
      case 'waiting':
      case 'loading':
      case 'load-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        return false;
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'ready':
      case 'updating':
      case 'edit-form-submission-error':
      case 'previewing-updating':
        return (
          modifiableExperiences(context, state).deletedExperienceStores.length === 0 &&
          modifiableExperiences(context, state).experienceStores.length != 0
        );
    }
  }

  selectedExperience = (
    context: ModifiableExperiencesContext,
    experienceResource: ExperienceResource,
  ): Maybe<LearningDesignExperienceStore> => {
    const state = this.state;
    switch (state.kind) {
      case 'waiting':
      case 'loading':
      case 'load-error':
      case 'ready-deep-links':
      case 'loading-for-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        return nothing();
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'ready':
      case 'updating':
      case 'edit-form-submission-error':
      case 'previewing-updating':
        return find(
          (store) => store.represents(experienceResource),
          modifiableExperiences(context, state).experienceStores,
        );
    }
  };

  @computed
  get learningCollectionEditFormResource(): Maybe<LearningCollectionEditFormResource> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'load-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
        return nothing();
      case 'updating-product-license':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'ready':
      case 'updating':
      case 'edit-form-submission-error':
      case 'previewing-updating':
        return just(this.state.learningCollectionEditFormResource);
    }
  }

  @computed
  get template(): Maybe<FormTemplate> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'loading-for-deep-links':
      case 'load-error':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        return nothing();
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'updating':
      case 'edit-form-submission-error':
      case 'previewing-updating':
        return just(this.state.template);
    }
  }

  @computed
  get currentFormHash(): Maybe<string> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'loading-for-deep-links':
      case 'load-error':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'ready':
      case 'updating':
      case 'edit-form-submission-error':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        return nothing();
      case 'previewing':
      case 'previewing-updating':
        return just(previewShaHashGenerator(this.state));
    }
  }

  learningDesignExperienceStores = (
    learningDesign: LearningDesignResource | ProductCollectionResource,
  ): ReadonlyArray<LearningDesignExperienceStore> => {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'loading-for-deep-links':
      case 'load-error':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        return [];
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'ready':
      case 'updating':
      case 'edit-form-submission-error':
      case 'previewing-updating':
        return just(this.state.sections)
          .andThen(find((s) => s.resource.payload.id === learningDesign.payload.id))
          .map((s) => s.learningDesignExperiences.experienceStores)
          .getOrElseValue([]);
    }
  };

  experienceStores = (
    context: ModifiableExperiencesContext,
  ): ReadonlyArray<LearningDesignExperienceStore> => {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'loading-for-deep-links':
      case 'load-error':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        return [];
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'ready':
      case 'updating':
      case 'edit-form-submission-error':
      case 'previewing-updating':
        return modifiableExperiences(context, this.state).experienceStores;
    }
  };

  @computed
  get onDemandExperienceStores(): ReadonlyArray<LearningDesignExperienceStore> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'loading-for-deep-links':
      case 'load-error':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        return [];
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'ready':
      case 'updating':
      case 'edit-form-submission-error':
      case 'previewing-updating':
        return this.state.browseAllExperiences.experienceStores.filter(
          ({ experienceResource }) => experienceResource.payload.programInstanceOptions.onDemand,
        );
    }
  }

  deletedExperienceStores = (
    context: ModifiableExperiencesContext,
  ): ReadonlyArray<LearningDesignExperienceStore> => {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'loading-for-deep-links':
      case 'load-error':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        return [];
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'updating':
      case 'edit-form-submission-error':
      case 'previewing-updating':
        return modifiableExperiences(context, this.state).deletedExperienceStores;
    }
  };

  deletedOnDemandExperienceStores = (
    context: ModifiableExperiencesContext,
  ): ReadonlyArray<LearningDesignExperienceStore> => {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'loading-for-deep-links':
      case 'load-error':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        return [];
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'updating':
      case 'edit-form-submission-error':
      case 'previewing-updating':
        return modifiableExperiences(context, this.state).deletedExperienceStores.filter(
          ({ experienceResource }) => experienceResource.payload.programInstanceOptions.onDemand,
        );
    }
  };

  @computed
  get canSave(): boolean {
    const state = this.state;
    switch (state.kind) {
      case 'waiting':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'ready':
      case 'loading-for-deep-links':
      case 'updating':
      case 'load-error':
      case 'previewing-updating':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
      case 'saving-details':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
        return false;
      case 'editing-details':
      case 'building':
      case 'edit-form-submission-error':
      case 'previewing':
        return fromEmpty(
          findField('name', 'string', state.template.fields)
            .map(stringFieldOutValue)
            .getOrElseValue(''),
        ).cata({
          Nothing: () => false,
          Just: () => true,
        });
    }
  }

  @computed
  get productCollections(): ReadonlyArray<ProductCollectionResource> {
    return this.learningCollectionEditFormResource
      .map(
        (learningCollectionEditFormResource) =>
          learningCollectionEditFormResource.payload.productCollections,
      )
      .getOrElse(() => []);
  }

  @computed
  get learningDesigns(): ReadonlyArray<LearningDesignResource> {
    return this.learningCollectionEditFormResource
      .map((learningCollectionEditFormResource) =>
        filterProductCollectionBy<LearningDesignResource>(
          'learning-design',
          learningCollectionEditFormResource.payload.productCollections,
        ),
      )
      .getOrElse(() => []);
  }

  @computed
  get recommendationEngines(): ReadonlyArray<RecommendationEngineResource> {
    return this.learningCollectionEditFormResource
      .map((learningCollectionEditFormResource) =>
        filterProductCollectionBy<RecommendationEngineResource>(
          'recommendation-engine',
          learningCollectionEditFormResource.payload.productCollections,
        ),
      )
      .getOrElse(() => []);
  }

  @computed
  get learningCollectionFormResource(): Maybe<LearningCollectionEditFormResource> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'load-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
        return nothing();
      case 'updating-product-license':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'ready':
      case 'updating':
      case 'edit-form-submission-error':
      case 'previewing-updating':
        return just(this.state.learningCollectionEditFormResource);
    }
  }

  @computed
  get coachingProducts(): ReadonlyArray<ExperienceResource> {
    return this.learningCollectionEditFormResource
      .map((learningCollectionEditFormResource) =>
        allCoachingExperiencesFrom(learningCollectionEditFormResource.payload),
      )
      .getOrElseValue([]);
  }

  @computed
  get ecommerceEnabled(): boolean {
    return this.learningCollectionEditFormResource
      .andThen((learningCollectionEditFormResource) =>
        isEcommerceEnabled(learningCollectionEditFormResource.payload.commerceType).map(() => true),
      )
      .getOrElseValue(false);
  }

  @action
  handleOnDragEndBrowseAll = (result: DropResult) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'previewing-updating':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
      case 'editing-details':
      case 'saving-details':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
        break;
      case 'previewing':
      case 'building':
      case 'edit-form-submission-error':
        if (result.destination) {
          this.state.browseAllExperiences.experienceStores = reorder(
            this.state.browseAllExperiences.experienceStores,
            result.source.index,
            result.destination.index,
          );
        }
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  handleOnDragEndExperience = (section: Section) => (result: DropResult) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'previewing-updating':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
      case 'editing-details':
      case 'saving-details':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
        break;
      case 'previewing':
      case 'building':
      case 'edit-form-submission-error':
        if (result.destination) {
          section.learningDesignExperiences.experienceStores = reorder(
            section.learningDesignExperiences.experienceStores,
            result.source.index,
            result.destination.index,
          );
        }
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  handleOnDragEndSections = (result: DropResult) => {
    switch (this.state.kind) {
      case 'waiting':
      case 'ready':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'loading':
      case 'updating':
      case 'load-error':
      case 'previewing-updating':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
      case 'editing-details':
      case 'saving-details':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
        break;
      case 'previewing':
      case 'building':
      case 'edit-form-submission-error':
        if (result.destination) {
          this.state.sections = reorder(
            this.state.sections,
            result.source.index,
            result.destination.index,
          );
        }
        break;
      default:
        assertNever(this.state);
    }
  };

  @computed
  get wizardItems(): Maybe<WizardStepItem[]> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'loading-for-deep-links':
      case 'load-error':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
        return nothing();
      case 'editing-details':
      case 'saving-details':
        return just([
          { label: 'Start', step: 1, state: 'completed' },
          { label: 'Describe', step: 2, state: 'current' },
          { label: 'Customize', step: 3, state: 'unstarted' },
          { label: 'Preview', step: 4, state: 'unstarted' },
          { label: 'Share', step: 5, state: 'unstarted' },
        ]);
      case 'ready':
      case 'updating':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'updating-product-license':
        return just([
          { label: 'Start', step: 1, state: 'completed' },
          { label: 'Describe', step: 2, state: 'completed' },
          { label: 'Customize', step: 3, state: 'current' },
          { label: 'Preview', step: 4, state: 'unstarted' },
          { label: 'Share', step: 5, state: 'unstarted' },
        ]);
      case 'previewing':
      case 'previewing-updating':
        return just([
          { label: 'Start', step: 1, state: 'completed' },
          { label: 'Describe', step: 2, state: 'completed' },
          { label: 'Customize', step: 3, state: 'completed' },
          { label: 'Preview', step: 4, state: 'current' },
          { label: 'Share', step: 5, state: 'unstarted' },
        ]);
      case 'finishing':
      case 'sharing':
        return just([
          { label: 'Start', step: 1, state: 'completed' },
          { label: 'Describe', step: 2, state: 'completed' },
          { label: 'Customize', step: 3, state: 'completed' },
          { label: 'Preview', step: 4, state: 'completed' },
          { label: 'Share', step: 5, state: 'current' },
        ]);
      case 'finished':
      case 'edit-form-submission-error':
        return nothing();
    }
  }

  @computed
  get inPreviewUpdatingState(): boolean {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'loading-for-deep-links':
      case 'load-error':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'ready':
      case 'updating':
      case 'edit-form-submission-error':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        return false;
      case 'previewing-updating':
        return true;
    }
  }

  @computed
  get browseAll(): boolean {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'load-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
        return false;
      case 'updating-product-license':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finished':
      case 'ready':
      case 'updating':
      case 'previewing-updating':
      case 'finishing':
      case 'edit-form-submission-error':
        return findField('browse_all', 'boolean', this.state.template.fields)
          .map((field) => field.value)
          .getOrElseValue(false);
    }
  }

  @computed
  get browseAllCustomized(): boolean {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'load-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
        return false;
      case 'updating-product-license':
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finished':
      case 'updating':
      case 'previewing-updating':
      case 'finishing':
      case 'edit-form-submission-error':
        return (
          JSON.stringify(
            this.state.learningCollectionEditFormResource.payload.allExperiences.map(
              (experience) => experience.payload.id,
            ),
          ) !==
          JSON.stringify(
            this.state.browseAllExperiences.experienceStores.map(
              ({ experienceResource }) => experienceResource.payload.id,
            ),
          )
        );
    }
  }

  @computed
  get availableExtensionIds(): number[] {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'load-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        return [];
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finished':
      case 'updating':
      case 'previewing-updating':
      case 'finishing':
      case 'edit-form-submission-error':
        return this.state.learningCollectionEditFormResource.payload.availableExtensionIds;
    }
  }

  @computed
  get selectedProgramIds(): number[] {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'load-error':
      case 'loading-for-deep-links':
      case 'ready-deep-links':
      case 'loading-for-invitations':
      case 'ready-invitations':
      case 'updating-product-license':
        return [];
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'ready':
      case 'updating':
      case 'edit-form-submission-error':
      case 'previewing-updating':
        return this.state.learningCollectionEditFormResource.payload.selectedScheduledPrograms;
    }
  }

  @computed
  get links(): ReadonlyArray<Link> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'loading-for-deep-links':
      case 'load-error':
      case 'loading-for-invitations':
        return [];
      case 'ready-invitations':
      case 'ready-deep-links':
        return this.state.learningCollectionResource.links;
      case 'ready':
      case 'editing-details':
      case 'saving-details':
      case 'building':
      case 'choosing-learning-designs':
      case 'adding-learning-design':
      case 'deleting-section':
      case 'previewing':
      case 'sharing':
      case 'finishing':
      case 'finished':
      case 'updating':
      case 'edit-form-submission-error':
      case 'previewing-updating':
      case 'updating-product-license':
        return this.state.learningCollectionEditFormResource.links;
    }
  }
}

export default LearningCollectionEditStore;
